import { DefaultResType, ErrorType } from "../../types/common/response";
import { baseApi } from "../../util/axiosConfig";
import { OrdersCreateSubjectElementType, OrdersCreateType } from "../../pages/order/orders/create/interface";
import { OrdersDownloadType, OrdersSearchParamType, OrdersUpdateProgressType, PostOrderShipmentExportShipmentInvoiceExcelApiForm } from "../../pages/order/orders/list/interface";
import { BasicSearchType } from "../../types/common/basic";
import { usePostOrderShipmentUploadInvoice } from '../../pages/order/orders/list/hooks/usePostOrderShipmentUploadInvoice';
import {
    OrdersDetailSubjectElementType,
    OrdersDetailSubjectType,
    OrdersDetailType,
    OrdersShipmentDeliveryConfirmFileType,
    OrdersShipmentDeliveryConfirmTaxInfoType,
    OrdersShipmentDeliveryConfirmType,
    OrdersShipmentExchangeRateSearchParamType,
    OrdersExportShipmentExcelParamReqType
} from "../../pages/order/orders/detail/interface";

// ===========================================================================
const shipmentUrl = "api/order/shipment/";

export async function readShipmentList(data: OrdersSearchParamType): Promise<DefaultResType> {
    try {
        return await baseApi.post(shipmentUrl + "read_shipment_list", data);
    } catch (err: any) {
        return err?.response;
    }
}

export async function readShipmentDetail(data: string): Promise<DefaultResType> {
    try {
        return await baseApi.post(shipmentUrl + "read_shipment_detail", { newShipmentId: data });
    } catch (err: any) {
        return err?.response;
    }
}

export async function readOrderSubjects(data: string): Promise<DefaultResType> {
    try {
        return await baseApi.post(shipmentUrl + "read_order_subjects", { id: data });
    } catch (err: any) {
        return err?.response;
    }
}

export async function readOrderPta(data: string): Promise<DefaultResType> {
    try {
        return await baseApi.post(shipmentUrl + "read_order_pta", { newLCustomerValue: data });
    } catch (err: any) {
        return err?.response;
    }
}

export async function readPreviousPayment(data: BasicSearchType & {
    newLEnduserValue: string
}): Promise<DefaultResType> {
    try {
        return await baseApi.post(shipmentUrl + "read_previous_payment", data);
    } catch (err: any) {
        return err?.response;
    }
}

export async function readShipmentDeliveryConfirm(data: string): Promise<DefaultResType> {
    try {
        return await baseApi.post(shipmentUrl + "read_shipment_delivery_confirm", { id: data });
    } catch (err: any) {
        return err?.response;
    }
}

export async function readShipmentExchangeRate(data: OrdersShipmentExchangeRateSearchParamType): Promise<DefaultResType> {
    try {
        return await baseApi.post(shipmentUrl + "read_shipment_exchange_rate", data);
    } catch (err: any) {
        return err?.response;
    }
}

export async function updateOrderProgress(data: OrdersUpdateProgressType[]): Promise<DefaultResType> {
    try {
        return await baseApi.post(shipmentUrl + "update_order_progress", data);
    } catch (err: any) {
        return err?.response;
    }
}

export async function saveOrder(data: OrdersDetailType): Promise<DefaultResType> {
    try {
        return await baseApi.post(shipmentUrl + "save_order", data);
    } catch (err: any) {
        return err?.response;
    }
}

export async function saveOrderSubjects(data: OrdersDetailSubjectElementType[]): Promise<DefaultResType> {
    try {
        return await baseApi.post(shipmentUrl + "save_order_subjects", data);
    } catch (err: any) {
        return err?.response;
    }
}

export async function updateShipmentExchangeRate(data: OrdersDetailSubjectType): Promise<DefaultResType> {
    try {
        return await baseApi.post(shipmentUrl + "update_shipment_exchange_rate", data);
    } catch (err: any) {
        return err?.response;
    }
}


export async function downloadShipmentFile(data: OrdersShipmentDeliveryConfirmFileType): Promise<Blob | ArrayBuffer | Uint8Array | string | ErrorType> {
    const response = await baseApi.post(shipmentUrl + "download_shipment_file", data, {
        responseType: "blob",
    });

    return response?.data;
}

export async function createDeliveryConfirmFile(data: FormData): Promise<DefaultResType> {
    try {
        return await baseApi.post(shipmentUrl + "create_delivery_confirm_file", data, { headers: { "Content-Type": "multipart/form-data" } });
    } catch (err: any) {
        return err?.response;
    }
}

export async function updateShipmentDeliveryConfirm(data: OrdersShipmentDeliveryConfirmType): Promise<DefaultResType> {
    try {
        return await baseApi.post(shipmentUrl + "update_shipment_delivery_confirm", data);
    } catch (err: any) {
        return err?.response;
    }
}

export async function deleteShipment(data: { id: string }[]): Promise<DefaultResType> {
    try {
        return await baseApi.post(shipmentUrl + "delete_shipment", data);
    } catch (err: any) {
        return err?.response;
    }
}

export async function deleteShipmentFile(data: OrdersShipmentDeliveryConfirmFileType): Promise<DefaultResType> {
    try {
        return await baseApi.post(shipmentUrl + "delete_shipment_file", data);
    } catch (err: any) {
        return err?.response;
    }
}

export async function exportShipmentExcel(data: OrdersExportShipmentExcelParamReqType): Promise<Blob | ArrayBuffer | Uint8Array | string | ErrorType> {
    const response = await baseApi.post(shipmentUrl + "export_shipment_excel", data, {
        responseType: "blob",
    });
    return response?.data;
}

/** 송장 내보내기 (엑셀 다운로드) */
export async function postOrderShipmentExportShipmentInvoiceExcelApi(
    data: PostOrderShipmentExportShipmentInvoiceExcelApiForm[]
): Promise<Blob> {
    const response = await baseApi.post(shipmentUrl + "export_shipment_invoice_excel", data, {
        responseType: "blob", // 파일 응답
    });
    return response.data; // Blob
}

/** 송장 가져오기 */
export async function postOrderShipmentUploadInvoiceApi(data: File): Promise<DefaultResType> {
    const formData = new FormData();
    formData.append('file', data); // ← 여기서 "file"은 API 명세에 명시된 key
    const response = await baseApi.post(shipmentUrl + "upload_invoice", formData, {
        headers: {
            'Content-Type': 'multipart/form-data',
        },
    });
    return response?.data;
}

// ===========================================================================
const orderUrl = "api/order/order/";

export async function createOrder(data: {
    orderDetailRes: OrdersCreateType,
    orderSubjectResList: OrdersCreateSubjectElementType[]
}): Promise<DefaultResType> {
    try {
        return await baseApi.post(orderUrl + "create_order", data);
    } catch (err: any) {
        return err?.response;
    }
}

export async function readShippingSelectBox(): Promise<DefaultResType> {
    try {
        return await baseApi.post(orderUrl + "read_shipping_select_box");
    } catch (err: any) {
        return err?.response;
    }
}

export async function readPaymentSelectBox(): Promise<DefaultResType> {
    try {
        return await baseApi.post(orderUrl + "read_payment_select_box");
    } catch (err: any) {
        return err?.response;
    }
}

export async function readCurrencyTypeSelectBox(): Promise<DefaultResType> {
    try {
        return await baseApi.post(orderUrl + "read_currency_type_select_box");
    } catch (err: any) {
        return err?.response;
    }
}

// ===========================================================================
const deadlineUrl = "api/order/deadline/";

export async function updateShipmentTaxInfo(data: OrdersShipmentDeliveryConfirmTaxInfoType): Promise<DefaultResType> {
    try {
        return await baseApi.post(deadlineUrl + "update_shipment_tax_info", data);
    } catch (err: any) {
        return err?.response;
    }
}